import { useCallback } from 'react'
import {
  BiDotsVerticalRounded,
  BiEditAlt,
  BiLink,
  BiLinkExternal,
  BiTrash,
} from 'react-icons/bi'
import { useNavigate } from 'react-router-dom'
import { MenuButton, MenuDivider, useClipboard } from '@chakra-ui/react'
import { IconButton, Menu, useToast } from '@opengovsg/design-system-react'

import { AdminDashboardEventListDto } from '~shared/dto'

import { useDashboardModalsContext } from '~features/dashboard/DashboardModalsProvider'
import { getUrlForPublicEventList } from '~features/dashboard/utils'

export type EventListActionsButtonGroupProps = {
  eventList: AdminDashboardEventListDto
  isEventListOwner: boolean
}

export const EventListActionsButtonGroup = ({
  eventList,
  isEventListOwner,
}: EventListActionsButtonGroupProps): JSX.Element => {
  const { onOpenDeleteEventListModal } = useDashboardModalsContext()
  const navigate = useNavigate()
  const toast = useToast()
  const publicEventListUrl = getUrlForPublicEventList(eventList.id)
  const { onCopy: copyLinkToClipboard } = useClipboard(publicEventListUrl)

  const handleCopyLink = useCallback(() => {
    copyLinkToClipboard()
    toast({
      status: 'success',
      description: 'Copied event list link.',
    })
  }, [copyLinkToClipboard, toast])

  return (
    <Menu placement={'bottom-end'} offset={[0, -4]}>
      <MenuButton
        as={IconButton}
        icon={<BiDotsVerticalRounded />}
        variant="clear"
        flex={1}
        borderStartRadius={0}
      />
      <Menu.List width={'170px'}>
        <Menu.Item
          icon={<BiEditAlt size="1.25rem" />}
          onClick={() => navigate(`/admin/event-list/${eventList.id}`)}
        >
          Edit
        </Menu.Item>
        <Menu.Item icon={<BiLink size="1.25rem" />} onClick={handleCopyLink}>
          Copy link
        </Menu.Item>
        <Menu.Item
          as="a"
          icon={<BiLinkExternal size="1.25rem" />}
          href={publicEventListUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          View live list
        </Menu.Item>
        {isEventListOwner && (
          <>
            <MenuDivider mt={2} mb={2} />
            <Menu.Item
              icon={
                <BiTrash size="1.25rem" color="interaction.critical.default" />
              }
              textColor="interaction.critical.default"
              onClick={() => onOpenDeleteEventListModal(eventList)}
            >
              Delete
            </Menu.Item>
          </>
        )}
      </Menu.List>
    </Menu>
  )
}
